import { ThemeName, themes } from "@/style/themes";
import { safeSessionStorage } from "@/utils/safeStorages";
import siteConfig from "@/variables";
import {
  ContentCodeItem,
  ContentCodeStatus,
  FulfillmentStatus,
  FulfillmentStatusResponse,
} from "@riot-private/commerce-contentcodes-client/lib";
import React, { useMemo, useState } from "react";
import * as API from "../api/contentCodes";
import {
  gameSpecificCodeValidationStatusMap,
  isGameSpecificCodeValidationStatus,
} from "../helpers/contentCodeStatusUtils";
import { useTheme } from "../helpers/devHelpers";
import { PATHS, navigate } from "../helpers/navigate";

type ContentCodeState = {
  theme: ThemeName;
  code?: string;
  contentCodeStatus?: ContentCodeStatus;
  fulfillmentStatus?: FulfillmentStatus;
  fulfillmentItems?: FulfillmentStatusResponse["items"];
  rewards: ContentCodeItem[];
  validateCode: (params: API.ContentCodeParams) => Promise<ContentCodeStatus>;
  getCodeStatus: (params: API.ContentCodeParams) => Promise<FulfillmentStatusResponse["status"]>;
  claimCode: (params: API.ContentCodeParams) => Promise<FulfillmentStatus>;
  cancelRedeem: () => void;
};

const ContentCodeContext = React.createContext<ContentCodeState>({} as ContentCodeState);

export const useContentCodeProvider = () => React.useContext(ContentCodeContext);

export type ContentCodeProviderProps = {
  children: React.ReactNode;
};

export const ContentCodeProvider = ({ children }: ContentCodeProviderProps) => {
  const themeQueryParam =
    typeof window !== "undefined" ? (new URLSearchParams(window.location.search).get("theme") as ThemeName) : "";
  const storedCode = safeSessionStorage.getItem(siteConfig.KEY_REDEEM_CODE) || "";
  const storedRewards = JSON.parse(safeSessionStorage.getItem(siteConfig.KEY_REDEEM_REWARDS) || "[]");
  const storedFulfillmentItems = JSON.parse(
    safeSessionStorage.getItem(siteConfig.KEY_REDEEM_FULFILLMENT_ITEMS) || "{}",
  );
  const storedValidationStatus = (safeSessionStorage.getItem(siteConfig.KEY_REDEEM_CODE_VALIDATION_STATUS) ||
    "") as ContentCodeStatus;

  const [code, setCode] = React.useState(storedCode);
  const [rewards, setRewards] = useState<ContentCodeItem[]>(storedRewards);
  const [contentCodeStatus, setContentCodeStatus] = useState<ContentCodeStatus>(storedValidationStatus);
  const [fulfillmentStatus, setFulfillmentStatus] = useState<FulfillmentStatus>();
  const [fulfillmentItems, setFulfillmentItems] = useState<FulfillmentStatusResponse["items"]>(storedFulfillmentItems);
  const { gameToTheme, setNewTheme } = useTheme();

  const validateCode = React.useCallback(async (params: API.ContentCodeParams) => {
    setCode(params.code as string);

    const data = await API.validateCode(params);
    // Ensure missing rewards key will be converted to empty array rather than undefined
    const { rewards = [], status } = data;

    // If no rewards, navigate to generic error page
    if (!rewards) {
      navigate(PATHS.REDEEM_SUMMARY_ERROR_GENERIC);
    }

    setRewards(rewards);
    // Use the first item to set the theme
    if (rewards.length) {
      setNewTheme(gameToTheme(rewards[0].game));
    } else if (isGameSpecificCodeValidationStatus(status)) {
      // For some statuses, even if the rewards are not returned, we can map to
      // a specific game context for the purpose of theming the error.
      setNewTheme(gameToTheme(gameSpecificCodeValidationStatusMap[status]));
    }
    safeSessionStorage.setItem(siteConfig.KEY_REDEEM_REWARDS, JSON.stringify(rewards));
    setContentCodeStatus(status);
    safeSessionStorage.setItem(siteConfig.KEY_REDEEM_CODE_VALIDATION_STATUS, status);

    return status;
  }, []);

  // TODO: rename this to `getCodeFulfillmentStatus` to avoid confusion with content code status
  const getCodeStatus = React.useCallback(async (params: API.ContentCodeParams) => {
    const data = await API.getCodeStatus(params);

    setFulfillmentStatus(data.status);
    setFulfillmentItems(data.items);
    safeSessionStorage.setItem(siteConfig.KEY_REDEEM_FULFILLMENT_ITEMS, JSON.stringify(data.items));

    return data.status;
  }, []);

  const claimCode = React.useCallback(async (params: API.ContentCodeParams) => {
    const data = await API.claimCode(params);

    setFulfillmentStatus(data.status);
    setFulfillmentItems(data.items);
    safeSessionStorage.setItem(siteConfig.KEY_REDEEM_FULFILLMENT_ITEMS, JSON.stringify(data.items));

    return data.status;
  }, []);

  const cancelRedeem = React.useCallback(() => {
    // remove redeem related session storage
    safeSessionStorage.removeItem(siteConfig.KEY_REDEEM_REWARDS);
    safeSessionStorage.removeItem(siteConfig.KEY_REDEEM_CODE);
    safeSessionStorage.removeItem(siteConfig.KEY_REDEEM_FULFILLMENT_ITEMS);
    safeSessionStorage.removeItem(siteConfig.KEY_REDEEM_THEME);
    safeSessionStorage.removeItem(siteConfig.KEY_REDEEM_CODE_VALIDATION_STATUS);
    setCode("");
    setRewards([]);
    setFulfillmentItems({});
    setFulfillmentStatus("" as FulfillmentStatus);
    setContentCodeStatus("" as ContentCodeStatus);
  }, []);

  const theme = useMemo(() => {
    if (themeQueryParam && themes[themeQueryParam]) {
      return themeQueryParam;
    }

    return rewards?.length > 0 ? gameToTheme(rewards?.[0]?.game) : "riot";
  }, [themeQueryParam, rewards]);

  return (
    <ContentCodeContext.Provider
      value={{
        theme,
        code,
        contentCodeStatus,
        fulfillmentStatus,
        fulfillmentItems,
        rewards,
        validateCode,
        getCodeStatus,
        claimCode,
        cancelRedeem,
      }}
    >
      {children}
    </ContentCodeContext.Provider>
  );
};
